import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseStateRegistration, stateRegistrationToStorage, type IeIndicator } from "@/utils/stateRegistration";

interface ClientStateRegistrationFieldProps {
  /** Valor guardado: só dígitos (contribuinte), "ISENTO", "NAO_CONTRIBUINTE" ou vazio (ainda não informado). */
  value: string;
  onChange: (value: string) => void;
  /** Marca o campo com erro (ex.: tentou avançar sem informar). */
  invalid?: boolean;
  /** Texto de apoio abaixo do campo. */
  hint?: string;
  idPrefix?: string;
}

/**
 * Inscrição estadual do cliente CNPJ (obrigatória: sem ela a NF-e de CNPJ contribuinte é recusada pela SEFAZ).
 * Usada no formulário do cliente (Infos da Festa) e no cadastro de evento manual do fornecedor, e grava no mesmo campo
 * (`event_client_info.client_state_registration`).
 */
export const ClientStateRegistrationField: React.FC<ClientStateRegistrationFieldProps> = ({
  value,
  onChange,
  invalid,
  hint = "Usada na nota fiscal da empresa. Na dúvida, veja no cartão CNPJ ou pergunte ao contador.",
  idPrefix = "client-ie",
}) => {
  const parsed = parseStateRegistration(value);
  const [choice, setChoice] = useState<IeIndicator | "">(parsed?.indicator ?? "");
  const [number, setNumber] = useState(parsed?.number ?? "");

  // Valor vindo de fora (cadastro carregado do banco): acompanha, sem brigar com o que a pessoa está digitando.
  useEffect(() => {
    const current = choice === "taxPayer" ? number.replace(/\D/g, "") : choice ? stateRegistrationToStorage({ indicator: choice, number: null }) : "";
    if (current === value) return;
    const next = parseStateRegistration(value);
    setChoice(next?.indicator ?? "");
    setNumber(next?.number ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextChoice: IeIndicator | "", nextNumber: string) => {
    setChoice(nextChoice);
    setNumber(nextNumber);
    if (nextChoice === "taxPayer") onChange(nextNumber.replace(/\D/g, ""));
    else onChange(nextChoice ? stateRegistrationToStorage({ indicator: nextChoice, number: null }) : "");
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor={`${idPrefix}-choice`} className={`text-sm ${invalid ? "text-destructive" : "text-muted-foreground"}`}>
        Inscrição estadual *
      </Label>
      <select
        id={`${idPrefix}-choice`}
        value={choice}
        onChange={(e) => emit(e.target.value as IeIndicator | "", number)}
        className={`h-10 w-full rounded-md border bg-background px-3 text-sm ${invalid ? "border-destructive" : "border-border"}`}
      >
        <option value="">Selecione</option>
        <option value="taxPayer">Tenho inscrição estadual</option>
        <option value="exempt">Isento de inscrição estadual</option>
        <option value="nonTaxPayer">Não sou contribuinte (não tenho)</option>
      </select>
      {choice === "taxPayer" && (
        <Input
          id={`${idPrefix}-number`}
          aria-label="Número da inscrição estadual"
          inputMode="numeric"
          autoComplete="off"
          placeholder="Número da inscrição estadual"
          value={number}
          onChange={(e) => emit("taxPayer", e.target.value.replace(/[^\d.\-/ ]/g, "").slice(0, 20))}
          className={invalid ? "border-destructive" : "border-border"}
        />
      )}
      {invalid && (
        <p className="text-xs text-destructive">
          {choice === "taxPayer" ? "Informe a inscrição estadual (2 a 14 números)." : "Escolha uma opção para a inscrição estadual."}
        </p>
      )}
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
};
